import { useCallback, useEffect, useState } from "react";
import { fetchAuditorAdmission } from "../api/auditorApi";
import { fetchAdminAdmission } from "../api/adminApi";
import { fetchCompanyAdmission } from "../api/companyApi";
import { fetchHospitalAdmission } from "../api/hospitalApi";
import type { CompanyAdmissionDetail } from "../api/types";
import { getAuthToken } from "../auth";

type AdmissionScope = "company" | "hospital" | "auditor" | "admin";

export function useAdmissionDetail(admissionId: number, scope: AdmissionScope) {
  const [admission, setAdmission] = useState<CompanyAdmissionDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken() || admissionId <= 0) {
      setUsingApi(false);
      setAdmission(null);
      setError(admissionId <= 0 ? "Identifiant d'admission invalide." : null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const row =
        scope === "hospital"
          ? await fetchHospitalAdmission(admissionId)
          : scope === "auditor"
            ? await fetchAuditorAdmission(admissionId)
            : scope === "admin"
              ? await fetchAdminAdmission(admissionId)
              : await fetchCompanyAdmission(admissionId);
      setAdmission(row);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger l'admission.";
      setError(message);
      setAdmission(null);
    } finally {
      setLoading(false);
    }
  }, [admissionId, scope]);

  useEffect(() => {
    void load();
  }, [load]);

  return { admission, loading, error, usingApi, reload: load };
}
